import React, { useState } from 'react';
import { Button, Notice, SegmentedControl } from '@origo/origo-ui';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { ApiError } from '../../api/client';
import { useRequestSync } from '../../api/hooks';
import {
    DIRECTION_LABEL,
    policyAllows,
    SyncDirection,
    type SyncDirectionPolicy,
    TriggerSource,
} from '../../api/types';

const DIRECTIONS: SyncDirection[] = [SyncDirection.AgolToCentral, SyncDirection.CentralToAgol];

interface SyncTriggerProps {
    /** Utelatt → fan-out til alle aktiverte koblinger. */
    bindingId?: string;
    /** Kort kontekst brukt i reason-feltet, f.eks. koblingnavn eller "alle". */
    contextLabel: string;
    /** Koblingens retningssperre. Bare tillatte retninger kan velges. Utelatt (fan-out) → begge. */
    policy?: SyncDirectionPolicy;
}

export const SyncTrigger: React.FC<SyncTriggerProps> = ({ bindingId, contextLabel, policy }) => {
    const allowed = DIRECTIONS.filter((d) => policyAllows(policy, d));
    const [direction, setDirection] = useState<SyncDirection>(allowed[0] ?? SyncDirection.AgolToCentral);
    const mutation = useRequestSync();

    const run = () => {
        mutation.mutate({
            direction,
            source: TriggerSource.Manual,
            reason: `Synkadmin manuell synk (${contextLabel})`,
            bindingId: bindingId ?? null,
        });
    };

    const result = mutation.data;
    const errorMessage = mutation.error instanceof ApiError
        ? mutation.error.message
        : mutation.error
            ? 'Kunne ikke be om synkronisering.'
            : null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-03)' }}>
            {allowed.length > 1 ? (
                <SegmentedControl
                    label="Synkroniseringsretning"
                    value={String(direction)}
                    onChange={(v) => setDirection(Number(v) as SyncDirection)}
                    options={allowed.map((d) => ({ value: String(d), label: DIRECTION_LABEL[d] }))}
                />
            ) : (
                <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
                    Retning: <strong>{DIRECTION_LABEL[direction]}</strong> (koblingen er låst til én retning)
                </p>
            )}
            {!bindingId && (
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-primary-70)' }}>
                    Koblinger som er låst til én retning, hoppes over i den andre.
                </p>
            )}
            <Button
                variant="primary"
                size="sm"
                leftIcon={<ArrowsClockwiseIcon size={16} weight="bold" />}
                onClick={run}
                disabled={mutation.isPending}
            >
                {mutation.isPending ? 'Sender…' : bindingId ? 'Kjør synk' : 'Synk alle aktiverte'}
            </Button>

            {result && !errorMessage && (
                <Notice variant="success">
                    {result.fanOutCount != null
                        ? `Synk bestilt for ${result.fanOutCount} kobling(er).`
                        : 'Synk bestilt. Worker plukker opp jobben.'}
                </Notice>
            )}
            {errorMessage && <Notice variant="danger">{errorMessage}</Notice>}
        </div>
    );
};
